import type { ReactElement } from 'react';
import { Fragment } from 'react';
import { useLocation } from '@gatsbyjs/reach-router';
import { Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemPrimaryText,
} from '@noah-labs/fe-shared-ui-components';
import { useLocalization } from 'gatsby-theme-i18n';
import type { ConfigItem } from './types';
import { StyledIcon, StyledListItemButton } from '../navbar/DesktopMenuItem';
import { LocalizedLinkWithRef } from './LocalizedLinkWithRef';
import { findByCode, storeLocale } from './utils';

type PpLanguageSwitch = {
  isSelected: boolean;
  onEnter: (label: string) => void;
  onLeave: () => void;
};

export function LanguageSwitch({
  isSelected,
  onEnter,
  onLeave,
}: PpLanguageSwitch): ReactElement | null {
  const theme = useTheme();
  const { config, locale } = useLocalization();
  const location = useLocation();

  if (!Array.isArray(config)) {
    return null;
  }

  const currentLanguage = findByCode(config as ConfigItem[], locale);
  const availableLanguages: ConfigItem[] = config.filter(
    (item: ConfigItem) => item.code !== locale,
  );

  return (
    <ListItem sx={{ width: '75px' }} onMouseLeave={onLeave}>
      <List disablePadding>
        <ListItem>
          <StyledListItemButton onMouseOver={(): void => onEnter('Locale')}>
            <ListItemPrimaryText color="text.dark" variant="navigationItem">
              {currentLanguage?.localName}
            </ListItemPrimaryText>
            <ListItemIcon>
              <StyledIcon isSelected={isSelected} size={12} />
            </ListItemIcon>
          </StyledListItemButton>
        </ListItem>
        {isSelected && (
          <Fragment>
            <Divider
              sx={{
                borderColor: theme.palette.websiteBrand.primary,
                marginBottom: 0.5,
                paddingTop: 0.5,
              }}
            />
            {availableLanguages.map((language) => (
              <ListItem key={language.code}>
                <StyledListItemButton
                  component={LocalizedLinkWithRef}
                  href={location.pathname.replace(/\/(es|lt)\//, '/')}
                  language={language.code as string}
                  onClick={(): void => {
                    storeLocale(language.code as string);
                  }}
                >
                  <ListItemPrimaryText color="text.dark" variant="navigationItem">
                    {language.localName}
                  </ListItemPrimaryText>
                </StyledListItemButton>
              </ListItem>
            ))}
          </Fragment>
        )}
      </List>
    </ListItem>
  );
}
